import React from 'react';

import { ContentBlock, RendererConfig, TextFormattingOptions } from '../types';

export abstract class BaseBlockRenderer<T extends ContentBlock = ContentBlock> {
  protected config: RendererConfig;
  protected textOptions: TextFormattingOptions;

  constructor(config: RendererConfig, textOptions: TextFormattingOptions) {
    this.config = config;
    this.textOptions = textOptions;
  }

  public render(block: T, index: number): React.ReactElement {
    this.validateBlock(block);
    return this.renderBlock(block, index);
  }

  protected abstract renderBlock(block: T, index: number): React.ReactElement;

  protected validateBlock(block: T): void {
    if (!block.type) {
      throw new Error(`Block is missing type`);
    }
  }

  protected formatText(text: string): React.ReactNode {
    if (!this.textOptions.enableMarkdown) {
      return text;
    }

    const parts = text.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`|```[\s\S]*?```|>.*?(?:\n|$))/g);

    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*')) {
        return <em key={index}>{part.slice(1, -1)}</em>;
      }
      if (part.startsWith('`') && part.endsWith('`') && !part.startsWith('```')) {
        return <code key={index}>{part.slice(1, -1)}</code>;
      }
      if (part.startsWith('```') && part.endsWith('```')) {
        return (
          <pre
            key={index}
            className="bg-gray-100 border border-gray-200 rounded-md p-3 my-2 font-mono text-sm overflow-x-auto"
          >
            <code>{part.slice(3, -3)}</code>
          </pre>
        );
      }
      if (part.startsWith('>')) {
        return (
          <blockquote
            key={index}
            className="border-l-4 border-blue-500 pl-4 my-2 italic text-neutral-60 bg-blue-50 py-2 rounded-r-md"
          >
            {this.formatText(part.slice(1).trim())}
          </blockquote>
        );
      }
      return part;
    });
  }

  protected getSpacingClasses(): string {
    switch (this.config.spacing) {
      case 'compact':
        return 'space-y-2';
      case 'relaxed':
        return 'space-y-6';
      default:
        return 'space-y-4';
    }
  }

  protected getAnimationClasses(): string {
    return this.config.enableAnimations ? 'animate-fade-in' : '';
  }
}

export class BlockRendererFactory {
  private config: RendererConfig;
  private textOptions: TextFormattingOptions;

  constructor(config: RendererConfig, textOptions: TextFormattingOptions) {
    this.config = config;
    this.textOptions = textOptions;
  }

  public async createRenderer(blockType: string): Promise<BaseBlockRenderer> {
    switch (blockType) {
      case 'image': {
        const { ImageBlockRenderer } = await import('../components/renderers/ImageBlockRenderer');
        return new ImageBlockRenderer(this.config, this.textOptions);
      }
      case 'heading': {
        const { HeadingBlockRenderer } = await import(
          '../components/renderers/HeadingBlockRenderer'
        );
        return new HeadingBlockRenderer(this.config, this.textOptions);
      }
      case 'paragraph': {
        const { ParagraphBlockRenderer } = await import(
          '../components/renderers/ParagraphBlockRenderer'
        );
        return new ParagraphBlockRenderer(this.config, this.textOptions);
      }
      case 'list': {
        const { ListBlockRenderer } = await import('../components/renderers/ListBlockRenderer');
        return new ListBlockRenderer(this.config, this.textOptions);
      }
      case 'quiz': {
        const { QuizBlockRenderer } = await import('../components/renderers/QuizBlockRenderer');
        return new QuizBlockRenderer(this.config, this.textOptions);
      }
      case 'code': {
        const { CodeBlockRenderer } = await import('../components/renderers/CodeBlockRenderer');
        return new CodeBlockRenderer(this.config, this.textOptions);
      }
      case 'video': {
        const { VideoBlockRenderer } = await import('../components/renderers/VideoBlockRenderer');
        return new VideoBlockRenderer(this.config, this.textOptions);
      }
      case 'audio': {
        const { AudioBlockRenderer } = await import('../components/renderers/AudioBlockRenderer');
        return new AudioBlockRenderer(this.config, this.textOptions);
      }
      case 'blockquote': {
        const { BlockquoteBlockRenderer } = await import(
          '../components/renderers/BlockquoteBlockRenderer'
        );
        return new BlockquoteBlockRenderer(this.config, this.textOptions);
      }
      case 'quote': {
        const { QuoteBlockRenderer } = await import('../components/renderers/QuoteBlockRenderer');
        return new QuoteBlockRenderer(this.config, this.textOptions);
      }
      case 'highlight': {
        const { HighlightBlockRenderer } = await import(
          '../components/renderers/HighlightBlockRenderer'
        );
        return new HighlightBlockRenderer(this.config, this.textOptions);
      }
      case 'claim': {
        const { ClaimBlockRenderer } = await import(
          '../components/renderers/ClaimBlockRenderer'
        );
        return new ClaimBlockRenderer(this.config, this.textOptions);
      }
      case 'gap': {
        const { GapBlockRenderer } = await import(
          '../components/renderers/GapBlockRenderer'
        );
        return new GapBlockRenderer(this.config, this.textOptions);
      }
      default:
        throw new Error(`Unknown block type: ${blockType}`);
    }
  }
}
